// Content for the APPROVED-user "Manage Card" view (Figma "Manage Card" page,
// desktop node 3077-6702 / mobile node 3115-8545). Shown INSTEAD of the
// animated marketing page when the HOME_SCREEN API returns status=APPROVED.
// Dynamic strings (headerText, ctaText, cardBackImg, ...) come from the API;
// everything here is the static section copy.

// Shape of the payments HOME_SCREEN API response this view is driven by.
export interface HomeScreenData {
	name: string;
	headerText: string;
	headerImg: string;
	cardBackImg: string;
	userName: string;
	poweredByImg: string;
	userFlow: string;
	showResumePopup: boolean;
	ctaText: string;
	userEligibleToApply: boolean;
	ineligibleLogo: string;
	ineligibleText: string;
	ineligibleSubText: string;
	status: 'APPROVED' | 'PENDING' | 'REJECTED' | (string & {});
	subText: string;
}

export const MANAGE_CARD_HERO = {
	// \n forces the MWEB hero heading onto two lines ("Your Card." / "Your
	// Control.") via whitespace-pre-line; web collapses it back to one flow
	// (whitespace-normal). See the h1 in manage-hero.tsx.
	title: 'Your Card.\nYour Control.',
	subtitle:
		'Manage card, set transaction limits, redeem reward points, view statement and more',
	// CTA label comes from the API (ctaText); this is the fallback label.
	ctaFallback: 'Manage Card',
} as const;

export const MANAGE_CARD_DEALS = {
	chip: 'Core Features',
	// '\n' marks where the heading breaks on MOBILE; desktop renders one line
	// (see SectionHeading). Adjust the break point freely.
	title: 'Exclusive Travel Deals\nFor Every Journey',
	subtitle: 'Access curated offers across flights, stays, buses and trains.',
	cards: [
		{ title: '10% Off on Flights', desc: 'Save up to ₹3,000 on bookings' },
		{ title: '10% Off on Buses', desc: 'Save up to ₹300 on bookings' },
		{ title: '10% Off on Hotels', desc: 'Save up to ₹3,000 on bookings' },
		{ title: '₹0 PG Fee on Trains', desc: 'No extra charges on payments' },
	],
} as const;

export const MANAGE_CARD_BENEFITS = {
	chip: 'Travel Benefits',
	heading: 'Real Rewards.\nReal Travel Benefits.',
	items: [
		{
			title: '16 Complimentary Lounge Visits',
			body: 'Enjoy lounge access across airports and railway stations throughout the year.',
		},
		{
			title: '1% Fuel Surcharge Waiver',
			body: '1% fuel surcharge waiver of up to ₹250 per month.',
		},
		{
			title: '2.5% Reward Points',
			body: 'Earn rewards on EVERY spend.',
		},
		{
			title: '₹2 Lakh Travel Insurance*',
			body: 'Stay covered on every trip with ₹2 lakh travel insurance protection when you need it most on your RuPay ixigo AU credit card.',
		},
		{
			title: '24×7 Concierge Support',
			body: 'A dedicated concierge at your service seamless travel planning, priority reservations, and instant support.',
		},
		{
			title: 'Zero forex markup',
			body: 'Ideal for international travel, the ixigo AU credit card comes with zero forex markup, helping you save on every spend abroad.',
		},
	],
} as const;

export const MANAGE_CARD_UNLOCK = {
	chip: 'Joining Benefits',
	// '\n' = mobile-only break (see SectionHeading); desktop stays one line.
	title: 'Exclusive Benefits\nfrom Day One',
	// Home (main marketing) variant — breaks into exactly TWO lines on every
	// device (passed with breakOnDesktop). The manage-card flow keeps `title`.
	titleHome: 'Exclusive Benefits\nfrom Day One',
	// 6-cell bento: 5 stat cards + 1 decorative image cell (account mockup).
	cards: [
		{
			value: '₹1000',
			label: 'ixigo money',
			desc: 'On successful activation of card.',
		},
		{
			value: '1000',
			label: 'Reward Points',
			desc: 'On successful activation of card.',
		},
		{
			value: '5000',
			label: 'Reward Points',
			desc: 'On spends of ₹75,000 or more per calendar quarter.',
		},
		{
			value: '5',
			label: 'Reward Points',
			desc: 'For every ₹200 spent online, offline or via QR Payment on UPI.',
		},
		{
			value: 'ixigo',
			label: 'Travel Benefits',
			desc: 'Unlock exclusive savings on flight, train, bus and hotel bookings on ixigo.',
		},
	],
	brandsCaption: 'Works with every brand',
} as const;

export const MANAGE_CARD_FAQ = {
	// '\n' breaks the heading into two lines; SectionHeader honours it on desktop
	// too via breakOnDesktop (main flow). The centred manage-card flow wraps
	// naturally regardless.
	title: 'Frequently Asked\nQuestions',
	items: [
		{
			title: 'Q. What is the ixigo AU Credit Card?',
			body: 'The ixigo AU Credit Card is a co-branded, lifetime-free travel credit card issued by AU Small Finance Bank in partnership with ixigo, available in Visa and RuPay variants.',
		},
		{
			title:
				'Q. What makes the ixigo AU Credit Card the best credit card in India?',
			body: 'Zero joining and annual fees, zero forex markup, airport and railway lounge access, rewards on UPI spends (RuPay variant), and exclusive ixigo discounts make it one of the most rewarding travel cards.',
		},
		{
			title:
				'Q. What are the various benefits of my lifetime free credit card?',
			body: 'Welcome benefits worth ₹1,500, up to 10% off on ixigo bookings, 16 annual lounge visits, fuel surcharge waiver, ₹2 lakh travel insurance (RuPay), and reward points on every spend.',
		},
		{
			title: 'Q. Who can apply for the ixigo AU Credit Card?',
			body: 'Indian residents aged 21–60 (salaried or self-employed) can apply. A CIBIL score of 700+ is recommended. The application is fully online and takes under 5 minutes.',
		},
		{
			title: 'Q. How do I manage my ixigo AU Credit Card?',
			body: 'Use the Manage Card dashboard to track transactions, set or change your PIN, enable transaction types, set limits, and raise support requests — all from one place.',
		},
	],
} as const;
